/**
 * Where a setting applies: Global, a region, or a country inside one.
 *
 * Settings resolve down this tree. A country inherits from its region, the
 * region from Global, and a value set at a level is an override of whatever
 * that level would otherwise inherit. Global is the firm-wide baseline, which
 * is why the portal stays one product: a country nobody has configured works
 * on Global's values, and a feature exists once whatever scope switches it on.
 *
 * A scope decides what is configured, never who may act - that is a
 * capability, in `@core/access`. A user's scope is their country claim, which
 * already chooses their market's valuation profile.
 */

export type ScopeLevel = 'global' | 'region' | 'country';

export interface ScopeNode {
  /** `global`, a region code (`APAC`), or an ISO 3166-1 alpha-2 country (`SG`). */
  id: string;
  level: ScopeLevel;
  label: string;
  /** `null` only for Global. */
  parent: string | null;
  /** ISO 4217, for a country - used for its format examples. */
  currency?: string;
  /**
   * Country codes that belong to this scope besides its id - for a market
   * that spans several countries, such as Greater China (CN, HK, MO, TW). A
   * user or a record in any of them resolves to this scope.
   */
  aliases?: readonly string[];
}

export const GLOBAL_SCOPE = 'global';

/**
 * The regions and the countries / markets the firm works in. Further
 * countries are added on the Settings page and stored with the settings, not
 * here.
 */
export const BUILT_IN_SCOPES: readonly ScopeNode[] = [
  { id: GLOBAL_SCOPE, level: 'global', label: 'Global', parent: null },

  { id: 'APAC', level: 'region', label: 'APAC', parent: GLOBAL_SCOPE },
  { id: 'EMEA', level: 'region', label: 'EMEA', parent: GLOBAL_SCOPE },
  { id: 'AMER', level: 'region', label: 'AMER', parent: GLOBAL_SCOPE },

  { id: 'IN', level: 'country', label: 'India', parent: 'APAC', currency: 'INR' },
  { id: 'SG', level: 'country', label: 'Singapore', parent: 'APAC', currency: 'SGD' },
  { id: 'AU', level: 'country', label: 'Australia', parent: 'APAC', currency: 'AUD' },
  /* A market, not one country: mainland China, Hong Kong, Macau and Taiwan. */
  { id: 'GC', level: 'country', label: 'Greater China', parent: 'APAC', aliases: ['CN', 'HK', 'MO', 'TW'] },

  { id: 'GB', level: 'country', label: 'United Kingdom', parent: 'EMEA', currency: 'GBP' },
  { id: 'FR', level: 'country', label: 'France', parent: 'EMEA', currency: 'EUR' },
  { id: 'NL', level: 'country', label: 'Netherlands', parent: 'EMEA', currency: 'EUR' },
  { id: 'DE', level: 'country', label: 'Germany', parent: 'EMEA', currency: 'EUR' },

  { id: 'US', level: 'country', label: 'United States', parent: 'AMER', currency: 'USD' },
  { id: 'CA', level: 'country', label: 'Canada', parent: 'AMER', currency: 'CAD' }
];

/** The built-in tree plus any countries added in settings, without duplicates. */
export function scopeTree(added: readonly ScopeNode[] = []): ScopeNode[] {
  const byId = new Map<string, ScopeNode>();

  for (const node of [...BUILT_IN_SCOPES, ...added]) {
    if (!byId.has(node.id)) {
      byId.set(node.id, node);
    }
  }

  return [...byId.values()];
}

/** The scope a scope id or a country code names - `HK` finds Greater China. */
export function findScope(nodes: readonly ScopeNode[], id: string | null | undefined): ScopeNode | undefined {
  if (!id) {
    return undefined;
  }

  const key = id === GLOBAL_SCOPE ? id : id.trim().toUpperCase();

  return nodes.find((node) => node.id === key) ?? nodes.find((node) => node.aliases?.includes(key));
}

/** From Global down to `id`, inclusive. An unknown id resolves to Global alone. */
export function scopeChain(nodes: readonly ScopeNode[], id: string | null | undefined): ScopeNode[] {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const chain: ScopeNode[] = [];
  let current = findScope(nodes, id);

  while (current && chain.length < 4) {
    chain.unshift(current);
    current = current.parent ? byId.get(current.parent) : undefined;
  }

  return chain.length > 0 ? chain : [byId.get(GLOBAL_SCOPE)!];
}

export function childrenOf(nodes: readonly ScopeNode[], id: string): ScopeNode[] {
  return nodes.filter((node) => node.parent === id);
}

/**
 * The scope a signed-in user's settings resolve at.
 *
 * Their country claim, where it names a country in the tree; otherwise the
 * service line they have picked in the toolbar, which is a region. Neither
 * being known is a real case - the tenant does not always send a country -
 * and lands on Global rather than on nothing.
 */
export function userScopeId(
  nodes: readonly ScopeNode[],
  countryClaim: string | null | undefined,
  workspaceRegion: string | null | undefined
): string {
  const country = findScope(nodes, countryClaim);

  if (country?.level === 'country') {
    return country.id;
  }

  if (workspaceRegion && nodes.some((node) => node.id === workspaceRegion && node.level === 'region')) {
    return workspaceRegion;
  }

  return GLOBAL_SCOPE;
}

/* --------------------------------------------------------------- resolving -- */

/** One level's say on a value - `undefined` means it inherits. */
export interface Layer<T> {
  /** A scope id, or `profile` / `default` and the like for the code-shipped layers. */
  source: string;
  value: T | undefined;
}

export interface Resolved<T> {
  value: T;
  /** The layer that decided it. */
  source: string;
}

/**
 * The first layer that says anything, strongest first; the fallback otherwise.
 * Every scoped setting - flags, formats, rule parameters - resolves through this.
 */
export function resolveLayers<T>(layers: readonly Layer<T>[], fallback: Resolved<T>): Resolved<T> {
  for (const layer of layers) {
    if (layer.value !== undefined) {
      return { value: layer.value, source: layer.source };
    }
  }

  return fallback;
}
